import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';
import { queryKeys } from '../../lib/queryClient';
import type {
  Organization,
  OrganizationFilters,
  OrganizationsResponse,
  CreateOrganizationDto,
  UpdateOrganizationDto,
  OrganizationWithStats,
  OrganizationStats,
} from '../../types/organization.types';

// ==================== Query Hooks ====================

/**
 * Получение списка организаций с фильтрацией и пагинацией
 */
export function useOrganizations(
  filters: OrganizationFilters = {},
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: queryKeys.organizations.list(filters),
    queryFn: async () => {
      const response = await apiClient.getOrganizations(filters);
      // Backend returns { items, total } but we need { data, total, page, limit, totalPages }
      const items = (response as any).items || response.data || [];
      const total = response.total || 0;
      const page = filters.page || 1;
      const limit = filters.limit || 20;
      return {
        data: items,
        total,
        page,
        limit,
        totalPages: Math.ceil(total / limit) || 1,
      } as OrganizationsResponse;
    },
    enabled: options.enabled !== false,
  });
}

/**
 * Получение организации по ID
 */
export function useOrganization(id: string, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.organizations.detail(id),
    queryFn: async () => {
      const response = await apiClient.getOrganization(id);
      return response as Organization;
    },
    enabled: enabled && !!id,
  });
}

/**
 * Получение организации со статистикой
 */
export function useOrganizationWithStats(id: string, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.organizations.withStats(id),
    queryFn: async () => {
      const response = await apiClient.getOrganizationWithStats(id);
      return response as OrganizationWithStats;
    },
    enabled: enabled && !!id,
  });
}

/**
 * Получение статистики организации
 */
export function useOrganizationStats(id: string, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.organizations.stats(id),
    queryFn: async () => {
      const response = await apiClient.getOrganizationStats(id);
      return response as OrganizationStats;
    },
    enabled: enabled && !!id,
  });
}

/**
 * Получение сотрудников организации
 */
export function useOrganizationEmployees(
  organizationId: string,
  filters: Record<string, any> = {},
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.organizations.employees(organizationId, filters),
    queryFn: () => apiClient.getOrganizationEmployees(organizationId, filters),
    enabled: enabled && !!organizationId,
  });
}

/**
 * Получение головной организации холдинга
 */
export function useHeadquartersOrganization(enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.organizations.headquarters(),
    queryFn: async () => {
      const response = await apiClient.getHeadquartersOrganization();
      return response as Organization;
    },
    enabled,
  });
}

// ==================== Mutation Hooks ====================

/**
 * Создание новой организации
 */
export function useCreateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateOrganizationDto) => apiClient.createOrganization(data),
    onSuccess: () => {
      // Инвалидируем список организаций
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.lists() });
      // Инвалидируем headquarters на случай если создана новая головная организация
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.headquarters() });
    },
  });
}

/**
 * Обновление организации
 */
export function useUpdateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateOrganizationDto }) =>
      apiClient.updateOrganization(id, data),
    onSuccess: (_, { id }) => {
      // Инвалидируем конкретную организацию и список
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.withStats(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.headquarters() });
    },
    // Optimistic update
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.organizations.detail(id) });

      const previousOrganization = queryClient.getQueryData<Organization>(
        queryKeys.organizations.detail(id)
      );

      if (previousOrganization) {
        queryClient.setQueryData(queryKeys.organizations.detail(id), {
          ...previousOrganization,
          ...data,
        });
      }

      return { previousOrganization };
    },
    onError: (_err, { id }, context) => {
      if (context?.previousOrganization) {
        queryClient.setQueryData(
          queryKeys.organizations.detail(id),
          context.previousOrganization
        );
      }
    },
  });
}

/**
 * Удаление (деактивация) организации
 */
export function useDeleteOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.deleteOrganization(id),
    onSuccess: () => {
      // Инвалидируем списки
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.headquarters() });
    },
  });
}

// ==================== Utility Hooks ====================

/**
 * Получение списка организаций для селектора (без пагинации)
 */
export function useOrganizationsForSelect(enabled: boolean = true) {
  return useQuery({
    queryKey: [...queryKeys.organizations.all(), 'select'],
    queryFn: async () => {
      const response = await apiClient.getOrganizations({ limit: 100, is_active: true });
      // Backend returns { items, total } but we need array
      const items = (response as any).items || response.data || [];
      return items.map((org: Organization) => ({
        id: org.id,
        name: org.name,
        bin: org.bin,
        is_headquarters: org.is_headquarters,
      }));
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 минут - организации меняются редко
  });
}
